/**
 * DOCUMENTATION:
 * Component: VectorDatabases
 * Purpose: Teaching In-Memory Vector Search for static content.
 * * * Note for Junior Developers:
 * - Local Search: We are doing the 'Search' in the user's browser.
 * - Memory Store: Storing vectors in an array instead of a server.
 * - Cost: $0.00.
 */

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Box, Card, Typography, Grid, Button, styled, Stack, LinearProgress } from "@mui/material";
import StorageIcon from '@mui/icons-material/Storage';
import MemoryIcon from '@mui/icons-material/Memory';

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor: "#0a0519",
    paddingTop: theme.spacing(12),
    paddingBottom: theme.spacing(10),
    color: "white",
}));

const InfoCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(30, 20, 50, 0.8)",
    borderRadius: "16px",
    padding: theme.spacing(4),
    border: "1px solid rgba(52, 168, 83, 0.2)",
    color: "white",
}));

export default function VectorDatabases() {
    const [isIndexing, setIsIndexing] = useState(false);

    const simulateIndexing = () => {
        setIsIndexing(true);
        setTimeout(() => setIsIndexing(false), 3000);
    };

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography variant="h3" fontWeight={900} color="#34A853">
                    LOCAL VECTOR STORAGE
                </Typography>
                <Typography variant="subtitle1" sx={{ opacity: 0.7 }}>
                    RAG Without the Database Costs
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 8 } }}>
                <Grid item xs={12} md={6}>
                    <InfoCard>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <MemoryIcon sx={{ color: "#34A853" }} />
                            <Typography variant="h5" fontWeight={800}>In-Memory Search</Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ opacity: 0.8, lineHeight: 1.8, mb: 3 }}>
                            When your data is hard-coded in the frontend, you don't need a cloud DB.
                            We can use the <strong>MemoryVectorStore</strong> from LangChain.js to index
                            your lessons in the user's RAM.
                        </Typography>

                        <Box sx={{ p: 2, bgcolor: "rgba(0,0,0,0.3)", borderRadius: "8px", mb: 3 }}>
                            <Typography variant="caption" color="#34A853" fontWeight={700}>PROS:</Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>• Zero Server Cost<br/>• Privacy (Data stays in browser)<br/>• Sub-millisecond search</Typography>
                        </Box>

                        <Button
                            variant="contained"
                            fullWidth
                            onClick={simulateIndexing}
                            sx={{ bgcolor: "#34A853", color: "white" }}
                        >
                            {isIndexing ? "INDEXING RAM..." : "SIMULATE LOCAL INDEXING"}
                        </Button>
                        {isIndexing && <LinearProgress sx={{ mt: 2, bgcolor: "rgba(255,255,255,0.1)", "& .MuiLinearProgress-bar": { bgcolor: "#34A853" } }} />}
                    </InfoCard>
                </Grid>

                <Grid item xs={12} md={6}>
                    <InfoCard sx={{ borderColor: "rgba(66, 133, 244, 0.2)" }}>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <StorageIcon sx={{ color: "#4285F4" }} />
                            <Typography variant="h5" fontWeight={800}>Data Chunking</Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ opacity: 0.8, mb: 3 }}>
                            Because LLMs have a limit on how much they can read, we must "chunk" your
                            hard-coded code into smaller strings.
                        </Typography>



                        <Box sx={{ mt: 2, p: 2, bgcolor: "rgba(255,255,255,0.03)", borderRadius: "8px", fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {`const chunks = text.split(". "); // Simple Chunking`}
                        </Box>
                    </InfoCard>
                </Grid>

                <Grid item xs={12} textAlign="center">
                    <Link to="/ai-dashboard" style={{ textDecoration: "none" }}>
                        <Button variant="contained" sx={{ bgcolor: "#34A853", color: "white", fontWeight: 800, px: 6, borderRadius: "999px" }}>
                            BACK TO DASHBOARD
                        </Button>
                    </Link>
                </Grid>
            </Grid>
        </PageWrapper>
    );
}
